import React, { useEffect, useRef, JSX } from 'react';
import styled from 'styled-components';

const ScrollingSection = styled.div`
  margin-left: -16px;
  padding-left: 16px;

  overflow-x: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 0;
    height: 0;
  }

  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;

  display: flex;
`;

export type CarouselProps = {
  children: JSX.Element[];
};

const HorizontalScroller: React.FC<CarouselProps> = ({ children }: CarouselProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Used for click and drag scrolling on desktop
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      // Let mobile scroll natively
      return;
    }
    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.pageX - el.offsetLeft;
      scrollStart = el.scrollLeft;
      el.classList.add('dragging');
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault(); // prevent accidental text selection
      const x = e.pageX - el.offsetLeft;
      const walk = x - startX; // drag speed
      el.scrollLeft = scrollStart - walk;
    };

    const stopDragging = () => {
      isDragging = false;
      el.classList.remove('dragging');
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseup', stopDragging);
    el.addEventListener('mouseleave', stopDragging);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseup', stopDragging);
      el.removeEventListener('mouseleave', stopDragging);
    };
  }, []);

  return <ScrollingSection ref={scrollRef}>{children}</ScrollingSection>;
};

export default HorizontalScroller;
